/**
 * tm/flow/NowBar.tsx — 업체별 관리 › 업무 탭 「지금 단계」 막대 (2026-10-12 · 4차 시안)
 *  지금 단계 이름 · 상태(n일 지남 · 내 차례 / 업체·FP 회신 대기 n일째) + 버튼(✓ 완료 → 다음 단계 · +3일 (10/14) · +7일 · 날짜 · 회신 대기 ▾ · ⋯)
 *  공고 마감(밀리지 않는 단계)이 뒤에 있으면 그 날짜를 알려 준다
 */
import type { Company, Task } from '../types';
import type { FlowBody } from '../api';
import { C, T, R } from '../components/ui';
import { useFlowActions, TEAL, TEAL_DK } from './FlowActions';
import { claimText, curStep, diffDays, kstToday, md, missingResult, sortedSteps, stepEx, waitWho } from './flowLogic';

export default function NowBar({ task, comp, run, fpPhone, fpName }: {
  task: Task; comp: Company; run: (task: Task, body: FlowBody, label?: string) => Promise<boolean>; fpPhone?: string; fpName?: string;
}) {
  const cur = curStep(task);
  // 단계는 다 끝났는데 결과 단계에 결과가 없다 — 「결과를 골라 주세요」(승인 · 미승인 → 다시 신청 · 마감) · 2026-10-18 지도사 「빠짐없이 입력하도록」
  const pending = cur ? null : missingResult(task);
  const step = cur || pending;
  const today = kstToday();
  const ex = stepEx(step);
  const waiting = task.status === 'FP요청대기' || (step && step.status === '회신대기');
  const { bar, panel } = useFlowActions({ task, comp, step, kind: waiting ? 'wait' : 'mine', run, fpPhone, fpName, showDates: true, datePick: true, panelIndent: 14, compact: true, resultOnly: !!pending });
  if (!step && task.status !== 'FP요청대기') return null;
  const due = step && step.due ? String(step.due).slice(0, 10) : '';
  const d = due ? diffDays(today, due) : null;
  const ss = sortedSteps(task);
  const idx = step ? ss.findIndex(x => x.id === step.id) : -1;
  const fixedAhead = ss.slice(idx + 1).find(x => stepEx(x).fixed_due && x.due);
  const waitDays = waiting ? Math.max(0, diffDays(String(ex.wait_since || task.updated_at || today).slice(0, 10), today)) : 0;
  const postponed = Number(ex.postponed) || 0;

  const state = pending
    ? <span style={{ padding: '2px 8px', borderRadius: 10, background: '#FFF7ED', color: '#B45309', fontSize: T.xs, fontWeight: 700 }}>결과를 골라 주세요</span>
    : waiting
    ? <span style={{ padding: '2px 8px', borderRadius: 10, background: waitDays >= 14 ? TEAL_DK : '#E1F8F5', color: waitDays >= 14 ? '#fff' : TEAL, fontSize: T.xs, fontWeight: 700 }}>{waitWho(ex, task)} {waitDays}일째</span>
    : d === null ? <span style={{ fontSize: T.xs, color: C.muted }}>기한 없음 · 내 차례</span>
      : <span style={{ fontSize: T.xs, fontWeight: 700, color: d < 0 ? C.red : d === 0 ? '#B45309' : C.sub }}>{d < 0 ? `${-d}일 지남` : d === 0 ? '오늘' : `D-${d}`} · {md(due)} <span style={{ padding: '1px 6px', borderRadius: 10, background: C.accentLt, color: C.accent, marginLeft: 4 }}>내 차례</span></span>;

  return (
    <div style={{ borderTop: `1px solid ${C.border}`, background: waiting ? '#F2FBFA' : d !== null && d < 0 ? '#FFFBFB' : C.bgAlt }}>
      <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: T.xs, color: C.muted }}>{pending ? '결과 비어 있음' : '지금 단계'}</span>
        <b style={{ fontSize: T.sm }}>{step ? step.name : 'FP 요청 대기'}</b>
        {state}
        {postponed > 0 && <span style={{ fontSize: T.xs, fontWeight: 700, color: postponed >= 2 ? '#B45309' : C.sub }}>미룸 {postponed}회</span>}
        <span style={{ flex: 1 }} />
        {bar}
      </div>
      {waiting && claimText(ex) && (
        <div style={{ margin: '0 14px 8px', padding: '6px 10px', borderRadius: R.sm, background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: T.xs, color: '#92400E', fontWeight: 600 }}>
          {claimText(ex)} · 확인하고 「회신 받음」을 눌러 주세요
        </div>
      )}
      {(ex.wait_question || ex.nudged_at) && waiting && (
        <div style={{ padding: '0 14px 8px', fontSize: T.xs, color: C.sub }}>
          {ex.wait_question ? `물어본 것 · ${ex.wait_question}` : ''}{ex.nudged_at ? `${ex.wait_question ? ' · ' : ''}${md(ex.nudged_at)} 재촉` : ''} · 둘 중 누구든 회신하면 끝
        </div>
      )}
      {!waiting && fixedAhead && (
        <div style={{ margin: '0 14px 8px', padding: '6px 10px', borderRadius: R.sm, background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: T.xs, color: '#92400E' }}>
          📌 공고 마감 「{fixedAhead.name}」 {md(String(fixedAhead.due).slice(0, 10))}은 밀리지 않아요
          {d !== null && d < 0 ? ` · 지금 완료해도 ${-d}일 늦은 만큼 그 전까지 준비할 날이 줄어요` : ''}
        </div>
      )}
      {panel}
    </div>
  );
}
